import { getLanguages } from "@/api/languages/languages-services";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import RichTextEditor from "@/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Loader from "@/components/utils/loader";
import { useStore } from "@/store";
import {
  capitalizeFirstLetter,
  decodeUnicodeEscapes,
  normalizeLocalizedTexts,
  normalizePolicyType,
} from "@/utils/policy-text";
import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
  identifier: z.string().optional(),
  title: z.record(
    z.string(),
    z.string().min(1, { message: "Este campo es requerido" }),
  ),
  policyText: z.record(
    z.string(),
    z.string().min(1, { message: "Este campo es requerido" }),
  ),
});

const CreatePolicyForm = ({ onClose }) => {
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [questionLanguages, setQuestionLanguages] = useState([]);
  const createPrivacyPolicy = useStore((state) => state.createPrivacyPolicy);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      identifier: "Privacy",
      title: undefined,
      policyText: undefined,
    },
  });

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languages = await getLanguages();
        setQuestionLanguages(languages);
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    };
    fetchLanguages();
  }, []);

  const onSubmit = async (values) => {
    setLoading(true);
    const { identifier, title, policyText } = values;
    const sendData = {
      type: normalizePolicyType(identifier) || "Privacy",
      preTexts: normalizeLocalizedTexts(title),
      postTexts: normalizeLocalizedTexts(policyText),
    };
    try {
      await createPrivacyPolicy(sendData);
      toast.success(t("alertDialog.toast.policyCreatedSuccess"));
      form.reset({
        identifier: "Privacy",
        title: {},
        policyText: {},
      });
      onClose?.();
    } catch (error) {
      console.error("Error al crear la política de privacidad:", error);
      toast.error(t("alertDialog.toast.policyCreatedError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="w-full flex justify-center">
      <Card className="p-6 w-full bg-slate-50">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.privacyPoicy.language")}</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      setSelectedLanguage(value);
                    }}
                    value={selectedLanguage}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar idioma" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {questionLanguages.map((language) => (
                        <SelectItem
                          key={language.id}
                          value={language.code.toString()}
                        >
                          {language.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="identifier"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.privacyPoicy.identifier")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Privacy"
                      value={field.value ?? "Privacy"}
                      onChange={(event) =>
                        field.onChange(
                          capitalizeFirstLetter(
                            decodeUnicodeEscapes(event.target.value),
                          ),
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`title.${selectedLanguage}`}
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>
                    {t("forms.privacyPoicy.title")} (
                    {selectedLanguage.toUpperCase()})
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(event) =>
                        field.onChange(decodeUnicodeEscapes(event.target.value))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`policyText.${selectedLanguage}`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("forms.privacyPoicy.description")} (
                    {selectedLanguage.toUpperCase()})
                  </FormLabel>
                  <FormControl>
                    <RichTextEditor
                      key={`policy-create-${selectedLanguage}`}
                      editorKey={`policy-create-${selectedLanguage}`}
                      value={field.value || ""}
                      tall={true}
                      onChange={(data) => field.onChange(data)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="mt-4 bg-primary hover:bg-primary-600"
            >
              {loading ? (
                <Loader style={"buttonLoader"} />
              ) : (
                t("buttonTexts.create")
              )}
            </Button>
          </form>
        </Form>
      </Card>
    </section>
  );
};
export default CreatePolicyForm;
